import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  DraggableList,
  Icon,
  List,
  ModalActionButton,
  ModalCancelButton,
  ModalContent,
  ModalContentActions,
  ModalContentBody,
  ModalContentFooter,
  ModalContentHeader,
  ModalOverlay,
  ModalPortal,
  ModalRoot,
  useDraggableList,
} from '@socialchorus/shared-ui-components';
import MojoApiFactory from '../../../../config/api-factory';
import { AssistantResponse } from '../../../../models/assistant/interfaces/AssistantResponse';
import { Service } from '../../../../models/assistant/interfaces/subject/Service';
import { applyShortcutOrder } from '../homepage-shortcuts';
import { createToastMessage, useToastMessages } from '../../toaster/toaster';
import Style from './edit-shortcut-order-modal.module.scss';
import classNames from 'classnames';

type EditShortcutOrderModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shortcuts: AssistantResponse<Service>[];
  homepageId: string;
  moduleId: string;
  currentOrder?: number[];
};

export const EditShortcutOrderModal = ({
  open,
  onOpenChange,
  shortcuts,
  homepageId,
  moduleId,
  currentOrder,
}: EditShortcutOrderModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { setToastMessage } = useToastMessages();

  const orderedShortcuts = applyShortcutOrder(shortcuts, currentOrder);

  const { items, handleDragEnd } = useDraggableList({
    initialItems: orderedShortcuts,
    itemIdFn: (item) => item.id,
  });

  const { mutate: saveOrder, isLoading: isSaving } = useMutation({
    mutationFn: async (shortcutOrder: number[]) => {
      return MojoApiFactory.optimus.put(
        `web_homepage/${homepageId}/modules/${moduleId}/user_customization`,
        { data: { shortcut_order: shortcutOrder } }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['customHomepageConfig'],
      });
      queryClient.invalidateQueries({
        queryKey: ['customHomepageById', homepageId],
      });
      onOpenChange(false);
    },
    onError: () => {
      setToastMessage(
        createToastMessage({
          text: t('shortcuts.reorder_error'),
          type: 'error',
        })
      );
    },
  });

  const handleSave = () => {
    const orderedIds = items
      .map((item) => Number(item.listItem.id))
      .filter((id) => !Number.isNaN(id));
    saveOrder(orderedIds);
  };

  return (
    <ModalRoot open={open} onOpenChange={onOpenChange} mode="dialog">
      <ModalPortal>
        <ModalOverlay />
        <ModalContent size="medium">
          <ModalContentHeader
            className={Style.Header}
            title={t('shortcuts.edit_order')}
            description={t('shortcuts.edit_order_description')}
          />
          <ModalContentBody>
            <DraggableList.Root
              className={Style.List}
              droppableId="shortcut-order"
              onDragEnd={handleDragEnd}
              gap="sm"
            >
              {items.map((item, index) => (
                <DraggableList.Item
                  key={item.listItem.id}
                  id={item.listItem.id}
                  index={index}
                  asChild
                >
                  <List.Item hoverable>
                    <Icon>drag_indicator</Icon>
                    <img
                      className={Style.Image}
                      src={item.listItem.subject.image_url}
                      alt=""
                    />
                    <div
                      className={classNames(
                        Style.Name,
                        'text-body text-black-90'
                      )}
                    >
                      {item.listItem.name}
                    </div>
                  </List.Item>
                </DraggableList.Item>
              ))}
            </DraggableList.Root>
          </ModalContentBody>
          <ModalContentFooter>
            <ModalContentActions>
              <ModalCancelButton label={t('common.cancel')} />
              <ModalActionButton
                label={t('shortcuts.save_changes')}
                onClick={handleSave}
                disabled={isSaving}
              />
            </ModalContentActions>
          </ModalContentFooter>
        </ModalContent>
      </ModalPortal>
    </ModalRoot>
  );
};
